// LAYOUT-SPEC §0 — Glossary (SAD-857)
//
//   ONE QUESTION  What does this acronym in my contract stand for, and where
//                 does the contract say so?
//   PRIMARY OBJECT  The program's terms (Archetype B: a collection).
//   PRIMARY VERB    Find a term; open the page that defines it.
//   WORST-CASE VOLUME  ~300 terms (an award with its attachments). One page,
//                      client search.
//
// The terms come from the program's own documents: what they define in print,
// and the acronyms they use without defining, confirmed by Jev. Nothing here
// is a hard-coded list. Below them, the links between terms and teams, which
// a person with execution write can add, edit and remove.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDataTable, SpecraListPage, SpecraPageHeader, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { BookA } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { EmptyState, ErrorState } from '../_shared/components/PageStates';
import { GlossaryLinks } from './GlossaryLinks';
import { GlossaryDocumentViewer, primaryRef, useGlossaryDocuments } from './GlossaryProvider';
import { useGlossary } from './GlossaryText';
const KIND_LABEL = {
    other: 'Term',
    team: 'Team',
    agency: 'Agency',
    product: 'Product',
    role: 'Role',
    funding_pot: 'Funding source'
};
/** Whether an entry matches a search: its term or any of its meanings. */ export function entryMatches(e, q) {
    if (!q) return true;
    const needle = q.toLowerCase();
    return e.term.toLowerCase().includes(needle) || e.senses.some((s)=>s.expansion.toLowerCase().includes(needle));
}
export function GlossaryPage() {
    const { programId, program } = useProgramContext();
    const glossary = useGlossary();
    const docs = useGlossaryDocuments(programId);
    const [sp, setSp] = useSearchParams();
    const query = sp.get('q') ?? '';
    const [viewing, setViewing] = useState(null);
    const projectId = program?.zouProjectId;
    const entries = glossary?.entries ?? [];
    const visible = useMemo(()=>entries.filter((e)=>entryMatches(e, query)), [
        entries,
        query
    ]);
    const conflicts = entries.filter((e)=>e.conflict).length;
    const documents = useMemo(()=>{
        const ids = new Set();
        for (const e of entries)for (const s of e.senses)for (const r of s.refs)ids.add(r.documentId);
        return ids.size;
    }, [
        entries
    ]);
    const setQuery = (v)=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            if (v) n.set('q', v);
            else n.delete('q');
            return n;
        }, {
            replace: true
        });
    const columns = [
        {
            id: 'term',
            header: 'Term',
            className: 'whitespace-nowrap',
            cell: (e)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium text-foreground",
                            children: e.term
                        }),
                        e.conflict ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: "The program's documents define this term two ways. Each meaning shows where it is printed.",
                            children: /*#__PURE__*/ _jsx("span", {
                                children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "warning",
                                    "data-testid": Testids.glossary.conflictBadge,
                                    children: "Two meanings"
                                })
                            })
                        }) : null
                    ]
                })
        },
        {
            id: 'meaning',
            header: 'Stands for',
            cell: (e)=>/*#__PURE__*/ _jsx("div", {
                    className: "space-y-[var(--space-1)]",
                    children: e.senses.map((s)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap items-center gap-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: s.expansion || '—'
                                }),
                                s.inferred ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                                    content: "No document defines it; a reader proposed this from how the documents use it, and a judge confirmed it.",
                                    children: /*#__PURE__*/ _jsx("span", {
                                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "neutral",
                                            "data-testid": Testids.glossary.inferredBadge,
                                            children: "Read from use"
                                        })
                                    })
                                }) : null
                            ]
                        }, s.ids[0]))
                })
        },
        {
            id: 'kind',
            header: 'Kind',
            cell: (e)=>/*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: [
                        ...new Set(e.senses.map((s)=>KIND_LABEL[s.kind] ?? s.kind))
                    ].join(', ')
                })
        },
        {
            id: 'source',
            header: 'Source',
            cell: (e)=>/*#__PURE__*/ _jsx("div", {
                    className: "space-y-[var(--space-1)]",
                    children: e.senses.map((s)=>{
                        const ref = primaryRef(s);
                        const doc = ref ? docs.get(ref.documentId) : undefined;
                        if (!ref) return null;
                        return /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-[var(--space-2)]",
                            children: [
                                projectId && doc?.fileId ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    className: "shrink-0",
                                    "data-testid": Testids.glossary.openPage,
                                    "data-page": ref.page || 1,
                                    onClick: ()=>setViewing({
                                            documentId: ref.documentId,
                                            page: ref.page || 1
                                        }),
                                    children: ref.page > 0 ? `Open page ${ref.page}` : 'Open'
                                }) : ref.page > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    className: "shrink-0",
                                    children: [
                                        "p. ",
                                        ref.page
                                    ]
                                }) : null,
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    className: "min-w-0 max-w-xs truncate",
                                    title: doc?.title,
                                    children: doc?.title ?? 'Source document'
                                })
                            ]
                        }, s.ids[0]);
                    })
                })
        }
    ];
    const empty = /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.glossary.empty,
        children: /*#__PURE__*/ _jsx(EmptyState, {
            icon: BookA,
            title: "No glossary yet",
            description: "Terms appear here as the award, its attachments, modifications and SOWs are read. A program read before the glossary existed fills in when its award is re-extracted."
        })
    });
    if (glossary?.error) {
        return /*#__PURE__*/ _jsx(SpecraListPage, {
            header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                titleSize: "home",
                title: "Glossary"
            }),
            children: /*#__PURE__*/ _jsx(ErrorState, {
                error: glossary.error
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraListPage, {
                "data-testid": Testids.glossary.page,
                header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    titleSize: "home",
                    title: "Glossary",
                    meta: entries.length,
                    description: entries.length ? `What the program's acronyms stand for, from ${documents} document${documents === 1 ? '' : 's'}${conflicts ? ` · ${conflicts} defined two ways` : ''}. Every underlined term in the program opens its entry.` : 'What the program’s acronyms stand for, read from its own documents.'
                }),
                toolbarProps: {
                    search: {
                        value: query,
                        onChange: (e)=>setQuery(e.target.value),
                        onClear: ()=>setQuery(''),
                        placeholder: 'Search terms or meanings…',
                        'data-testid': Testids.glossary.search
                    },
                    resultSummary: visible.length === entries.length ? `${entries.length} terms` : `${visible.length} of ${entries.length} shown`
                },
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-[var(--space-6)]",
                    children: [
                        !glossary?.isLoading && entries.length === 0 ? empty : /*#__PURE__*/ _jsx(SpecraDataTable, {
                            caption: "Glossary",
                            columns: columns,
                            rows: visible,
                            getRowId: (e)=>e.key,
                            loading: glossary?.isLoading,
                            rowTestId: Testids.glossary.row,
                            getRowProps: (e)=>({
                                    'data-term': e.key
                                }),
                            emptyTitle: `No terms match “${query}”`,
                            emptyDescription: "Search looks at each term and what it stands for."
                        }),
                        /*#__PURE__*/ _jsx(GlossaryLinks, {
                            programId: programId,
                            onOpenPage: projectId ? (documentId, page)=>setViewing({
                                    documentId,
                                    page
                                }) : undefined
                        })
                    ]
                })
            }),
            viewing && projectId ? /*#__PURE__*/ _jsx(GlossaryDocumentViewer, {
                programId: programId,
                projectId: projectId,
                target: viewing,
                onClose: ()=>setViewing(null)
            }) : null
        ]
    });
}
